/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useContext } from 'react';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import { NodeType } from '../../../../backendModels/report.model';
import DebouncedTextField from '../../../customs/debouncedTextField/DebouncedTextField';
import { CardId } from '../../../../models/diviCard';
import { useFreshCallback } from '../../../../utils/hooks';
import {
  getRefusalOfMeasuresTupleFor,
  RefusalOfMeasuresKeys,
  REMARKS_RECORD_ELEMENT_KEY,
  RemarksRecord,
} from '../../../../models/genericElements/remarks';
import { InputState } from '../../../../backendModels/general.model';
import { isGenericRecordDeletable } from '../../../../models/generic';
import { Draft } from 'immer';
import SignatureIcon from '../../../../assets/images/icons/signature.svg?react';
import { Typography } from '@mui/material';
import { useCSS } from '../../../../provider/CSSProvider';
import style from './RemarksNote.scss?inline';
import CheckboxList from '../../../customs/checkboxList/CheckboxList';

interface RemarksNoteProps {
  nodeType: NodeType;
}

export default function RemarksNote({ nodeType }: RemarksNoteProps) {
  useCSS(style);

  const { findRecordOrDefault, adaptRecord } = useContext(ReportsAPIContext);
  const record = findRecordOrDefault('generic', nodeType, REMARKS_RECORD_ELEMENT_KEY) as RemarksRecord;
  const refusalOfMeasuresRecord = findRecordOrDefault('generic', nodeType, REMARKS_RECORD_ELEMENT_KEY) as RemarksRecord;

  const refusalOfMeasuresItem: [RefusalOfMeasuresKeys, string][] = getRefusalOfMeasuresTupleFor([
    RefusalOfMeasuresKeys.massnahmenVerweigerung,
  ]);

  function handleRefusalOfMeasuresChange(value: RefusalOfMeasuresKeys[]) {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<RemarksRecord>, deleteRecord) => {
        draft.inputState = InputState.ENTERED;
        if (value.length !== 0) {
          draft.values = {
            ...draft.values,
            bemerkungenVerweigerung: {
              fieldType: 'multiSelect',
              options: value,
            },
          };
        } else {
          delete draft.values?.bemerkungenVerweigerung;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      REMARKS_RECORD_ELEMENT_KEY
    );
  }

  const handleNoteChange = useFreshCallback((value: string) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<RemarksRecord>, deleteRecord) => {
        draft.inputState = InputState.ENTERED;
        if (value !== '') {
          draft.values = {
            ...draft.values,
            bemerkungenText: {
              fieldType: 'text',
              text: value,
            },
          };
        } else {
          delete draft.values?.bemerkungenText;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      REMARKS_RECORD_ELEMENT_KEY
    );
  });

  return (
    <DiviFieldCard cardType={CardId.Remarks}>
      <CheckboxList
        className='symptoms-list'
        formControlLabelProps={{ className: 'refusal-of-measures-label' }}
        items={refusalOfMeasuresItem}
        selectedValues={
          refusalOfMeasuresRecord.inputState === InputState.ENTERED
            ? (refusalOfMeasuresRecord.values?.bemerkungenVerweigerung?.options ?? [])
            : []
        }
        onValuesChange={handleRefusalOfMeasuresChange}
      />
      <DebouncedTextField
        id='remarks-filled-textarea'
        label={'Bemerkungen (z.B. Verlauf, Hausarzt, Telefonnummer Angehörige, Notkompetenz-Maßnahmen)'}
        placeholder={'Bemerkungen (z.B. Verlauf, Hausarzt, Telefonnummer Angehörige, Notkompetenz-Maßnahmen)'}
        multiline
        fullWidth
        rows={10}
        onDebounceChange={handleNoteChange}
        value={record.inputState === InputState.ENTERED ? (record.values?.bemerkungenText?.text ?? '') : ''}
      />
      <div className='row signature'>
        <SignatureIcon />
        <Typography variant='body2'>Eine digitale Signatur wird automatisch generiert.</Typography>
      </div>
    </DiviFieldCard>
  );
}
